import React, { Fragment, useState, useRef, useEffect, useMemo } from "react";
import * as httpService from "../services/httpService";
import { Employee_Api_EndPoints } from "../global/end-points";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoadingSpinner from "../components/common/loadingSpinner";
import useActivityLogger from "../utils/useActivityLogger"; // ✅ logging hook

function AnimatedAmount({ value, duration = 800 }) {
  const [display, setDisplay] = useState(0);
  const start = useRef(0);

  useEffect(() => {
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setDisplay(start.current + (value - start.current) * progress);
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        start.current = value;
      }
    };
    requestAnimationFrame(step);
    // eslint-disable-next-line
  }, [value]);

  return (
    <span className="animated-amount">
      ${display.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
}

function InstaTransactions() {
  const { t } = useTranslation();
  const [isLoad, setIsLoad] = useState(false);
  const didMount = useRef(false);
  const [recentTransaction, setRecentTransaction] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [isTransactionLoading, setIsTransactionLoading] = useState(false);

  // // Privileges state
  // const [canRead, setCanRead] = useState(false);

  // ✅ Use OnBehalfUserId if present, else fallback
  const effectiveUserId =
    sessionStorage.getItem("OnBehalfUserId") || sessionStorage.getItem("UserId");
  const isOBO = !!sessionStorage.getItem("OnBehalfUserId");

  // ✅ activity logger
  const { logActivity, logClick, logApiCall } = useActivityLogger({
    moduleCode: isOBO ? "DPY" : "DPY",
  });

  useEffect(() => {
    if (!didMount.current) {
      // ✅ log page view
      logActivity({
        activity: "PAGE_VIEW",
        description: `${isOBO ? "OBO-" : ""}InstaTransactions Page View`,
        component: `${isOBO ? "OBO." : ""}Page.InstaTransactions`,
      });

      checkPrivilegesAndFetchTransactions();
      didMount.current = true;
    }
    // eslint-disable-next-line
  }, []);

  const checkPrivilegesAndFetchTransactions = async () => {
    setIsLoad(false);
    setIsTransactionLoading(true);

   
    if (effectiveUserId) {
      try {
        // ✅ log API request
        logApiCall(
          `${isOBO ? "OBO.API.GetTransactions" : "DPY.API.GetTransactions"}`,
          `${isOBO ? "OBO-" : ""}Get Transactions (request)`
        );

        const transResp = await httpService.Get(
          Employee_Api_EndPoints.GET_TRANSACTION_DETAILS,
          {
            UserId: Number(effectiveUserId),
          }
        );
        
        // ✅ Handle error responses gracefully
        if (transResp && transResp.status === 200 && !transResp.response && !transResp.code) {
          setRecentTransaction(transResp.data || []);
        } else {
          // Error response - set empty array
          setRecentTransaction([]);
        }

        // ✅ log API success
        logActivity({
          activity: "API_CALL",
          description: `${isOBO ? "OBO-" : ""}Get Transactions (success)`,
          component: `${isOBO ? "OBO." : ""}API.GetTransactions`,
        });
      } catch (error) {
        setRecentTransaction([]);
        // ✅ log API error
        logActivity({
          activity: "API_CALL",
          description: `${isOBO ? "OBO-" : ""}Get Transactions (error)`,
          component: `${isOBO ? "OBO." : ""}API.GetTransactions`,
        });
      }
    } else {
      setRecentTransaction([]);
    }

    setIsLoad(true);
    setIsTransactionLoading(false);
  };

  const [startDate, endDate] = dateRange;

  const filteredTransactions = useMemo(() => {
    if (startDate && endDate) {
      return recentTransaction.filter((t) => {
        const txDate = new Date(t.transferDate).setHours(0, 0, 0, 0);
        return (
          txDate >= startDate.setHours(0, 0, 0, 0) &&
          txDate <= endDate.setHours(0, 0, 0, 0)
        );
      });
    }
    return recentTransaction;
  }, [startDate, endDate, recentTransaction]);

  const totalAmount = useMemo(
    () =>
      filteredTransactions.reduce(
        (sum, transfer) => sum + (Number(transfer.amount) || 0),
        0
      ),
    [filteredTransactions]
  );
  const totalTips = useMemo(
    () =>
      filteredTransactions.reduce(
        (sum, transfer) => sum + (Number(transfer.tips) || 0),
        0
      ),
    [filteredTransactions]
  );
  const totalWages = useMemo(
    () =>
      filteredTransactions.reduce(
        (sum, transfer) => sum + (Number(transfer.wages) || 0),
        0
      ),
    [filteredTransactions]
  );

  const totalPages = Math.ceil(filteredTransactions.length / recordsPerPage);
  const paginatedTransactions = useMemo(
    () =>
      filteredTransactions.slice(
        (currentPage - 1) * recordsPerPage,
        currentPage * recordsPerPage
      ),
    [filteredTransactions, currentPage]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredTransactions]);

  if (!isLoad) return <LoadingSpinner />;
  // if (!canRead) return <AccessDenied />;
  if (isTransactionLoading) return <LoadingSpinner />;

  return (
    <Fragment>
      {isLoad && (
        <div className="dashboard-container">
          <h2 className="text-center color-white">{t("recentTrsTitle")}</h2>
          <br />

          <div className="filter-summary-row">
            <div className="date-filter-container">
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => {
                  setDateRange(update);

                  if (update && update[0] && update[1]) {
                    // ✅ log applying filter
                    logActivity({
                      activity: "FILTER_APPLY",
                      description: `${isOBO ? "OBO-" : ""}Applied Date Range Filter: ${update[0].toLocaleDateString()} - ${update[1].toLocaleDateString()}`,
                      component: `${isOBO ? "OBO.Filter.DateRange" : "DPY.Filter.DateRange"}`,
                    });
                  } else if (!update || (!update[0] && !update[1])) {
                    // ✅ log clearing filter
                    logActivity({
                      activity: "FILTER_CLEAR",
                      description: `${isOBO ? "OBO-" : ""}Cleared Date Range Filter`,
                      component: `${isOBO ? "OBO.Filter.DateRange" : "DPY.Filter.DateRange"}`,
                    });
                  }
                }}
                isClearable={true}
                placeholderText="Filter by date range"
                className="date-filter-input"
                maxDate={new Date()}
                minDate={startDate}
              />


            </div>
            <div className="totals-summary-box">
              <div><strong>{t("Total Amount")}:</strong></div>
              <div style={{ paddingLeft: 14 }}>
                <strong><AnimatedAmount value={totalAmount} /></strong>
              </div>
              <div><strong>{t("Total Tips") || "Total Tips"}:</strong></div>
              <div><strong><AnimatedAmount value={totalTips} /></strong></div>
              <div><strong>{t("Total Wages") || "Total Wages"}:</strong></div>
              <div style={{ paddingLeft: 14 }}>
                <strong><AnimatedAmount value={totalWages} /></strong>
              </div>
            </div>
          </div>

          <div className="table-container">
            {filteredTransactions.length > 0 ? (
              <div className="scrollable-table-wrapper">
                <table className="pay-info-table fixed-header">
                  <thead>
                    <tr>
                      <th>{t("Date")}</th>
                      <th>{t("Time")}</th>
                      <th>{t("Funded")}</th>
                      <th>{t("Tips")}</th>
                      <th>{t("Wages")}</th>
                      <th>{t("Amnt")}</th>
                      <th>{t("Convenience Fee")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTransactions.map((transfer, index) => {
                      const isOpen = selectedTransfer === index;
                      return (
                        <React.Fragment key={index}>
                          <tr
                            className="transaction-row"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setSelectedTransfer(isOpen ? null : index);
                              // ✅ log row click
                              logClick(
                                `${isOBO ? "OBO.Row.Transaction" : "DPY.Row.Transaction"}`,
                                `${isOBO ? "OBO-" : ""}Clicked Transaction Row`
                              );
                            }}
                          >
                            <td className="text-center">{transfer.transferDate}</td>
                            <td className="text-center">
                              {transfer.transferDateTime?.split("T")[1]?.split(".")[0] || "00:00:00"}
                            </td>
                            <td className="text-center">
                              {(String(transfer.transactedThrough || "").trim() === "-") ? "Card" : (transfer.transactedThrough || "")}
                            </td>
                            <td className="text-center">
                              ${transfer.tips ? transfer.tips.toFixed(2) : "0.00"}
                            </td>
                            <td className="text-center">
                              ${transfer.wages ? transfer.wages.toFixed(2) : "0.00"}
                            </td>
                            <td className="text-center">${transfer.amount.toFixed(2)}</td>
                            <td className="text-center">
                              ${transfer.transactionFee ? transfer.transactionFee.toFixed(2) : "0.00"}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>

                <div className="pagination-controls">
                  <button
                    onClick={() => {
                      const newPage = Math.max(currentPage - 1, 1);
                      setCurrentPage(newPage);

                      logActivity({
                        activity: "PAGINATION",
                        description: `${isOBO ? "OBO-" : ""}Pagination: Prev to page ${newPage}`,
                        component: `${isOBO ? "OBO.Pagination" : "DPY.Pagination"}`,
                      });
                    }}
                    disabled={currentPage === 1}
                  >
                    {"<"}
                  </button>

                  <span>
                    {currentPage} / {totalPages}
                  </span>

                  <button
                    onClick={() => {
                      const newPage = Math.min(currentPage + 1, totalPages);
                      setCurrentPage(newPage);

                      logActivity({
                        activity: "PAGINATION",
                        description: `${isOBO ? "OBO-" : ""}Pagination: Next to page ${newPage}`,
                        component: `${isOBO ? "OBO.Pagination" : "DPY.Pagination"}`,
                      });
                    }}
                    disabled={currentPage === totalPages}
                  >
                    {">"}
                  </button>
                </div>


              </div>
            ) : (
              <div className="nodata">
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p style={{ fontSize: '18px', fontWeight: 500, marginBottom: '10px', color: 'white' }}>
                    {t("nodatafoundmsg")}
                  </p>
                  {startDate && endDate && (
                    <p style={{ fontSize: '14px', color: 'white', marginTop: '8px' }}>
                      {t("nodatafoundmsgContext")}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Fragment>
  );
}

export default InstaTransactions;
